import * as React from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import FormBlock from 'modules/expenses/components/expenses/Detail/_newForm/Sections/FormBlock'
import { CustomizationProps } from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import { PermissionsProps } from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'
import { Expensable } from 'modules/expenses/types/Expensable'

interface Props extends PermissionsProps, CustomizationProps {
  expensable?: Expensable
}

const VerticalConnector = () => (
  <Box paddingLeft="s4" height="s12">
    <Box width="s2" height="s12" background="grey200" />
  </Box>
)

interface DestinationItemProps {
  locationName: string
  arrivalDate?: string
  showArrivalDate: boolean
}

const DestinationItem = ({ locationName, arrivalDate, showArrivalDate }: DestinationItemProps) => {
  const formattedDate = arrivalDate ? moment(arrivalDate).format('DD/MM/YYYY') : ''

  return (
    <Box flexDirection="row" gap="s8" alignItems="center">
      <Box width="s8" height="s8" background="radicalRed600" borderRadius={{ all: 'abs100' }} />
      <Box flexDirection="column" gap="s4" flexGrow>
        <Text.Normal size="200" weight="medium" color="grey1000">
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.destination')}
        </Text.Normal>
        <Box
          padding="s8"
          background="grey100"
          borderRadius={{ all: 'abs008' }}
          border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
        >
          <Text.Normal size="200" color="grey1000">
            {locationName}
          </Text.Normal>
        </Box>
      </Box>
      {showArrivalDate && arrivalDate && (
        <Box flexDirection="column" gap="s4" flexNoShrink>
          <Text.Normal size="200" weight="medium" color="grey1000">
            {i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.arrival')}
          </Text.Normal>
          <Box
            padding="s8"
            background="grey100"
            borderRadius={{ all: 'abs008' }}
            border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
          >
            <Text.Normal size="200" color="grey1000">
              {formattedDate}
            </Text.Normal>
          </Box>
        </Box>
      )}
    </Box>
  )
}

interface DateFieldProps {
  label: string
  date: string | null
  helperText?: string
}

const DateField = ({ label, date, helperText }: DateFieldProps) => {
  const formattedDate = date ? moment(date).format('DD/MM/YYYY') : ''

  return (
    <Box flexDirection="column" gap="s8">
      <Box flexDirection="row" alignItems="center" gap="s8">
        <Box width="s8" height="s8" background="grey400" borderRadius={{ all: 'abs100' }} />
        <Text.Normal size="200" weight="medium" color="grey1000">
          {label}
        </Text.Normal>
      </Box>
      <Box paddingLeft="s16">
        <Box
          padding="s8"
          background="grey100"
          borderRadius={{ all: 'abs008' }}
          border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
        >
          <Text.Normal size="200" color="grey1000">
            {formattedDate}
          </Text.Normal>
        </Box>
      </Box>
      {helperText && (
        <Box paddingLeft="s16">
          <Text.Normal size="100" color="grey600">
            {helperText}
          </Text.Normal>
        </Box>
      )}
    </Box>
  )
}

const Destinations = (props: Props) => {
  const { expensable } = props
  const perDiem = expensable?.perDiem
  const destinations = perDiem?.perDiemDestinations || []
  const startDate = perDiem?.startDate
  const endDate = perDiem?.endDate

  // Calculate trip duration for summary (must be before early return)
  const tripDuration = useMemo(() => {
    if (!startDate || !endDate) {
      return null
    }
    const start = moment(startDate)
    const end = moment(endDate)
    const days = end.diff(start, 'days') + 1
    const nights = days - 1
    return { days, nights }
  }, [startDate, endDate])

  if (!destinations || destinations.length === 0) {
    return null
  }

  return (
    <FormBlock
      title={i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.routes_and_dates')}
      subtitle={i18n.t(
        'expenses_v2.forms.germany_per_diem_modal.trip_details.routes_and_dates_description'
      )}
    >
      <Box flexDirection="column" gap="s16">
        <DateField
          label={i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.start_of_trip')}
          date={startDate}
          helperText={i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.start_helper')}
        />

        <VerticalConnector />

        {destinations.map((destination, index) => {
          // First destination doesn't show arrival date
          const isFirstDestination = index === 0
          const showArrivalDate = !isFirstDestination

          return (
            <React.Fragment key={destination.id}>
              <DestinationItem
                locationName={destination.locationName}
                arrivalDate={destination.arrivalDate}
                showArrivalDate={showArrivalDate}
              />
              {index < destinations.length - 1 && <VerticalConnector />}
            </React.Fragment>
          )
        })}

        <VerticalConnector />

        <DateField
          label={i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.end_of_trip')}
          date={endDate}
          helperText={i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.end_helper')}
        />

        {tripDuration && (
          <Box
            flexDirection="row"
            alignItems="center"
            gap="s8"
            padding="s12"
            background="viridian100"
            borderRadius={{ all: 'abs008' }}
          >
            <Icon.Small icon="info-circle" color="viridian1000" />
            <Text.Normal size="200" weight="medium" color="viridian1000">
              {i18n.t('expenses_v2.forms.germany_per_diem_modal.trip_details.trip_summary', {
                summary: `${tripDuration.days} day${tripDuration.days !== 1 ? 's' : ''} · ${
                  tripDuration.nights
                } night${tripDuration.nights !== 1 ? 's' : ''}`,
              })}
            </Text.Normal>
          </Box>
        )}
      </Box>
    </FormBlock>
  )
}

export default Destinations
